import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TablePagination,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Visibility,
  Add,
  CalendarToday,
  Person,
  MoreVert,
  Download,
  Close,
  CameraAlt,
  DescriptionOutlined as DocIcon,
} from "@mui/icons-material";
import { BASE_URL } from "../../../services/configUrls";
import GeoPhotoUploadModal from "./FDPEDP/GeoPhotoUploadModal";
import {
  checkGeoPhotoUploadEligibility,
  isEventWithinEndDate,
  isPrerequisitesViewable,
  checkPrerequisitesViewEligibility
} from "./FDPEDP/geoTagWatermarkUtil";
import SpocPrerequisitesDocuments from "./SpocPrerequisitesDocuments";
import DomainPrerequisitesViewModal from "./DomainPrerequisitesViewModal";

const TableView = ({
  filteredBookings,
  filter,
  setFilter,
  onBookingClick,
  canAddStudents,
  canViewStudents,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [downloadingCertificate, setDownloadingCertificate] = useState(false);
  const [certificateModalOpen, setCertificateModalOpen] = useState(false);
  const [certificatePdfUrl, setCertificatePdfUrl] = useState(null);
  const [certificateData, setCertificateData] = useState(null);

  // Geo Photo Modal State
  const [geoPhotoModalOpen, setGeoPhotoModalOpen] = useState(false);
  const [selectedGeoPhotoBooking, setSelectedGeoPhotoBooking] = useState(null);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const handleUploadGeoPhotos = (booking) => {
    const eligibility = checkGeoPhotoUploadEligibility(booking, "tech_camp");
    if (!eligibility.canUpload) {
      alert(eligibility.message);
      return;
    }
    setSelectedGeoPhotoBooking(booking);
    setGeoPhotoModalOpen(true);
  };

  // Download Banner Modal State
  const [downloadBannerModalOpen, setDownloadBannerModalOpen] = useState(false);
  const [selectedBannerBooking, setSelectedBannerBooking] = useState(null);

  const handleOpenDownloadBanner = (booking) => {
    setSelectedBannerBooking(booking);
    setDownloadBannerModalOpen(true);
  };

  // Domain Prerequisites Modal State
  const [domainPrerequisitesModalOpen, setDomainPrerequisitesModalOpen] =
    useState(false);
  const [selectedPrerequisitesBooking, setSelectedPrerequisitesBooking] =
    useState(null);

  const handleOpenDomainPrerequisites = (booking) => {
    const eligibility = checkPrerequisitesViewEligibility(booking);
    if (!eligibility.canView) {
      alert(eligibility.message);
      return;
    }
    setSelectedPrerequisitesBooking(booking);
    setDomainPrerequisitesModalOpen(true);
  };

  const colors = {
    fullyBooked: "#2196F3",
    partiallyBooked: "#9E9E9E",
    upcoming: "#4CAF50",
    started: "#FF9800",
    closed: "#9E9E9E",
  };

  const defaultCanAddStudents = (booking) => {
    if (!booking?.start_date) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dateStr =
      typeof booking.start_date === "string"
        ? booking.start_date.split("T")[0]
        : "";
    let startDate;
    if (dateStr.includes("-")) {
      const [year, month, day] = dateStr.split("-").map(Number);
      startDate = new Date(year, month - 1, day);
    } else {
      startDate = new Date(booking.start_date);
    }
    startDate.setHours(0, 0, 0, 0);

    const hasNotStarted = isNaN(startDate.getTime())
      ? false
      : today <= startDate;

    return (
      hasNotStarted &&
      !(
        booking.students_limit > 0 &&
        booking.students_added >= booking.students_limit
      )
    );
  };

  const checkCanAddStudents = canAddStudents || defaultCanAddStudents;

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getDateRange = (startDate, endDate) => {
    const start = formatDate(startDate);
    const end = formatDate(endDate);
    return start === end ? start : `${start} - ${end}`;
  };

  const isBookingFullyBooked = (booking) => {
    return (
      booking.students_limit > 0 &&
      booking.students_added >= booking.students_limit
    );
  };

  const isBookingPartiallyBooked = (booking) => {
    const hasParticipants = booking.students_added > 0;
    return hasParticipants && !isBookingFullyBooked(booking);
  };

  const getStatusChip = (booking) => {
    const getEventStatusChip = () => {
      if (!booking.event_status) return null;

      const statusConfig = {
        Upcoming: { color: colors.upcoming, label: "Upcoming" },
        Started: { color: colors.started, label: "Started" },
        Closed: { color: colors.closed, label: "Closed" },
      };

      const config = statusConfig[booking.event_status] || {
        color: colors.closed,
        label: booking.event_status,
      };

      return (
        <Chip
          label={config.label}
          size="small"
          sx={{
            backgroundColor: config.color,
            color: "white",
            fontWeight: 500,
            fontSize: "0.7rem",
          }}
        />
      );
    };

    const getBookingStatusChip = () => {
      if (booking.event_status === "Closed") {
        return null;
      }
      if (
        booking.event_status === "Canceled" ||
        booking.event_status === "Cancelled"
      ) {
        return null;
      }

      if (isBookingFullyBooked(booking)) {
        return null;
        // <Chip
        //   label="Fully Booked"
        //   size="small"
        //   sx={{
        //     backgroundColor: colors.fullyBooked,
        //     color: "white",
        //     fontWeight: 500,
        //     fontSize: "0.7rem",
        //   }}
        // />
      } else if (isBookingPartiallyBooked(booking)) {
        return null;
        // <Chip
        //   label="Booked"
        //   size="small"
        //   sx={{
        //     backgroundColor: colors.partiallyBooked,
        //     color: "white",
        //     fontWeight: 500,
        //     fontSize: "0.7rem",
        //   }}
        // />
      } else {
        return null;
        // <Chip
        //   label="Available"
        //   size="small"
        //   sx={{
        //     backgroundColor: "#4CAF50",
        //     color: "white",
        //     fontWeight: 500,
        //     fontSize: "0.7rem",
        //   }}
        // />
      }
    };

    return (
      <Box display="flex" gap={0.5} flexWrap="wrap">
        {getEventStatusChip()}
        {getBookingStatusChip()}
      </Box>
    );
  };

  const handleMenuOpen = (event, booking) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedBooking(booking);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBooking(null);
  };

  const handleMenuItemClick = (action) => {
    if (selectedBooking) {
      if (action === "view-certificate") {
        handleViewCertificate(selectedBooking);
      } else {
        onBookingClick(selectedBooking, action);
      }
    }
    handleMenuClose();
  };

  const handleViewCertificate = async (booking) => {
    try {
      setDownloadingCertificate(true);
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(
        `${BASE_URL}/event/institute-certificate/${booking.bookslot_id}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();

        if (data.certificate_base64) {
          // Convert base64 to blob
          const byteCharacters = atob(data.certificate_base64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "application/pdf" });

          // Create URL for PDF preview
          const url = window.URL.createObjectURL(blob);
          setCertificatePdfUrl(url);
          setCertificateData({ booking, blob });
          setCertificateModalOpen(true);
        } else {
          console.error("No certificate data received");
          alert("Certificate data not found");
        }
      } else {
        console.error("Failed to load certificate");
        alert("Failed to load certificate. Please try again.");
      }
    } catch (error) {
      console.error("Error loading certificate:", error);
      alert("An error occurred while loading the certificate.");
    } finally {
      setDownloadingCertificate(false);
    }
  };

  const handleDownloadCertificate = () => {
    if (certificateData) {
      const link = document.createElement("a");
      link.href = certificatePdfUrl;
      link.download = `Certificate_${certificateData.booking.domain_name}_${certificateData.booking.bookslot_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleCloseCertificateModal = () => {
    setCertificateModalOpen(false);
    if (certificatePdfUrl) {
      window.URL.revokeObjectURL(certificatePdfUrl);
      setCertificatePdfUrl(null);
    }
    setCertificateData(null);
  };

  const getActionButtons = (booking) => {
    return (
      <Tooltip title="More Actions">
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, booking)}
          sx={{
            color: "#616161",
            "&:hover": { backgroundColor: "#f5f5f5" },
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter to show only tech_camp events
  const techCampBookings = filteredBookings.filter(
    (booking) => booking.event_type === "tech_camp"
  );

  const paginatedBookings = techCampBookings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const hasCertificate = (booking) => {
    return booking.certificate_issue >= 1;
  };

  return (
    <Box>
      {/* <Typography variant="h4" gutterBottom align="center" sx={{ mt: 5 }}>
        Tech Camp Booking
      </Typography> */}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        mt={3}
      >
        <Typography variant="h6" color="text.secondary">
          {techCampBookings.length} Events
        </Typography>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
          border: "1px solid #e0e7ff",
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f8fafc" }}>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                Domain
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CalendarToday fontSize="small" />
                  Date Range
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Person fontSize="small" />
                  Trainer
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                Students
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                Status
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedBookings.map((booking) => (
              <TableRow
                key={booking.bookslot_id}
                sx={{
                  "&:hover": {
                    backgroundColor: "#f8fafc",
                  },
                  cursor: "pointer",
                }}
                onClick={() => onBookingClick(booking)}
              >
                <TableCell>
                  <Typography variant="body2">{booking.domain_name}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {getDateRange(booking.start_date, booking.end_date)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {booking.trainer_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {booking.trainer_email}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {`${booking.students_added}${
                      booking.students_limit > 0
                        ? ` / ${booking.students_limit}`
                        : ""
                    }`}
                  </Typography>
                </TableCell>
                <TableCell>{getStatusChip(booking)}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {getActionButtons(booking)}
                </TableCell>
              </TableRow>
            ))}
            {paginatedBookings.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No events found matching your filters
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={techCampBookings.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid #e0e0e0",
            ".MuiTablePagination-toolbar": {
              paddingRight: 2,
            },
          }}
        />
      </TableContainer>

      {/* Menu for actions */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={() => handleMenuItemClick("details")}>
          <ListItemIcon>
            <Visibility fontSize="small" sx={{ color: "#1976d2" }} />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>

        {selectedBooking && checkCanAddStudents(selectedBooking) && (
          <MenuItem onClick={() => handleMenuItemClick("add")}>
            <ListItemIcon>
              <Add fontSize="small" sx={{ color: "#4caf50" }} />
            </ListItemIcon>
            <ListItemText>Add Students</ListItemText>
          </MenuItem>
        )}

        {selectedBooking && hasCertificate(selectedBooking) && (
          <MenuItem
            onClick={() => handleMenuItemClick("view-certificate")}
            disabled={downloadingCertificate}
          >
            <ListItemIcon>
              {downloadingCertificate ? (
                <CircularProgress size={20} sx={{ color: "#ff9800" }} />
              ) : (
                <Visibility fontSize="small" sx={{ color: "#ff9800" }} />
              )}
            </ListItemIcon>
            <ListItemText>
              {downloadingCertificate ? "Loading..." : "View Certificate"}
            </ListItemText>
          </MenuItem>
        )}

        {/* Download Banner (Posters, Banners, Syllabus Files) - Shown till bookslot end date */}
        {selectedBooking && isEventWithinEndDate(selectedBooking) && (
          <MenuItem
            onClick={() => {
              handleOpenDownloadBanner(selectedBooking);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <Download fontSize="small" sx={{ color: "#1877f2" }} />
            </ListItemIcon>
            <ListItemText>Download Banner</ListItemText>
          </MenuItem>
        )}

        {/* Prerequisites (Domain Prerequisites Document) - Shown from 5 days before start date till end date */}
        {selectedBooking && isPrerequisitesViewable(selectedBooking) && (
          <MenuItem
            onClick={() => {
              handleOpenDomainPrerequisites(selectedBooking);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <DocIcon fontSize="small" sx={{ color: "#4f46e5" }} />
            </ListItemIcon>
            <ListItemText>Prerequisites</ListItemText>
          </MenuItem>
        )}

        {/* Upload Geo-Tagged Photos */}
        {selectedBooking && (() => {
          const eligibility = checkGeoPhotoUploadEligibility(selectedBooking, "tech_camp");
          const isEligible = eligibility.canUpload;

          return (
            <Tooltip
              title={
                !isEligible
                  ? eligibility.message
                  : eligibility.reminderMessage || "Upload or manage geo-tagged event photos"
              }
              arrow
              placement="left"
            >
              <span>
                <MenuItem
                  disabled={!isEligible}
                  onClick={() => {
                    if (!isEligible) return;
                    handleUploadGeoPhotos(selectedBooking);
                    handleMenuClose();
                  }}
                  sx={{
                    opacity: !isEligible ? 0.6 : 1,
                  }}
                >
                  <ListItemIcon>
                    <CameraAlt
                      fontSize="small"
                      sx={{ color: !isEligible ? "#94a3b8" : "#0284c7" }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Upload Geo Photos"
                    secondary={
                      !isEligible
                        ? eligibility.shortNotice
                        : undefined
                    }
                    secondaryTypographyProps={{
                      fontSize: "0.72rem",
                      color: "#dc2626",
                      fontWeight: 500,
                    }}
                  />
                </MenuItem>
              </span>
            </Tooltip>
          );
        })()}
      </Menu>

      {/* Certificate Modal */}
      <Dialog
        open={certificateModalOpen}
        onClose={handleCloseCertificateModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Certificate Preview</Typography>
            <IconButton
              onClick={handleCloseCertificateModal}
              size="small"
              sx={{ color: "text.secondary" }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ p: 0, display: "flex", flexDirection: "column" }}
        >
          {certificatePdfUrl ? (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#f5f5f5",
              }}
            >
              <iframe
                src={`${certificatePdfUrl}#toolbar=0`}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
                title="Certificate Preview"
              />
            </Box>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="400px"
            >
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCloseCertificateModal}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
          <Button
            onClick={handleDownloadCertificate}
            variant="contained"
            startIcon={<Download />}
            sx={{
              textTransform: "none",
              backgroundColor: "#ff9800",
              "&:hover": {
                backgroundColor: "#f57c00",
              },
            }}
          >
            Download Certificate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Geo-Tagging Photo Upload Modal */}
      <GeoPhotoUploadModal
        open={geoPhotoModalOpen}
        onClose={() => setGeoPhotoModalOpen(false)}
        booking={selectedGeoPhotoBooking}
        eventType="tech_camp"
        onSuccess={(res) => {
          setToast({
            open: true,
            message: res?.message?.trim() || "Photos uploaded successfully!",
            severity: "success"
          });
        }}
      />

      {/* Download Banner Modal (SpocPrerequisitesDocuments) */}
      <SpocPrerequisitesDocuments
        open={downloadBannerModalOpen}
        onClose={() => setDownloadBannerModalOpen(false)}
        booking={selectedBannerBooking}
        eventType="tech_camp"
      />

      {/* Domain Prerequisites Document Modal (View Only) */}
      <DomainPrerequisitesViewModal
        open={domainPrerequisitesModalOpen}
        onClose={() => setDomainPrerequisitesModalOpen(false)}
        booking={selectedPrerequisitesBooking}
        eventType="tech_camp"
      />

      {/* Toast Feedback */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "8px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
            fontWeight: 600,
            fontSize: "0.85rem"
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TableView;
